/**
 * ConnectionList
 * Responsabilidade: Exibir lista de conexões WhatsApp com ações de gerenciamento.
 * Usa ActionMenu para ações reutilizáveis seguindo padrão do projeto.
 */

import React, { useEffect, useMemo, useState } from 'react';
import DataTable, { Column } from '../../../../components/shared/DataTable';
import ActionMenu, { createDeleteAction, ActionMenuItem } from '../../../../components/shared/ActionMenu';
import { AppIcon } from '../../../../components/icons/AppIcon';
import { WhatsAppIcon } from '../../../../components/icons/CustomIcons';
import { canRenderRemoteAvatarUrl } from '../../../../utils/failedImageCache';

/** Avatar inline — só URL permanente (R2). CDN WhatsApp = 403, cai na inicial. */
function ConnectionPhoto({ name, url }: { name: string; url?: string }) {
    const src = url && canRenderRemoteAvatarUrl(url) ? url : undefined;
    const [failed, setFailed] = useState(false);
    useEffect(() => { setFailed(false); }, [src]);

    if (src && !failed) {
        return (
            <img
                src={src}
                alt={name}
                referrerPolicy="no-referrer"
                className="h-10 w-10 rounded-full border-2 border-border object-cover"
                onError={() => setFailed(true)}
            />
        );
    }

    return (
        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-blue-400 to-blue-600 text-sm font-semibold text-white">
            {name?.charAt(0)?.toUpperCase() || '?'}
        </div>
    );
}

interface WhatsAppConnection {
    id: string;
    number?: string;
    name: string;
    status: string;
    profilePhotoUrl?: string;
    instanceName?: string;
    config?: {
        instance_name?: string;
    };
}

interface ConnectionListProps {
    connections: WhatsAppConnection[];
    isLoading?: boolean;
    onAddConnection: () => void;
    onDeleteConnection: (id: string, name: string, instanceName: string) => void;
    onReconnect?: (instanceName: string, integrationId: string) => void;
    onDisconnect?: (instanceName: string) => void;
    searchTerm?: string;
    onSearchChange?: (value: string) => void;
}

const ConnectionList: React.FC<ConnectionListProps> = ({
    connections,
    isLoading,
    onAddConnection,
    onDeleteConnection,
    onReconnect,
    onDisconnect,
    searchTerm: externalSearch = '',
    onSearchChange,
}) => {
    const [internalSearch, setInternalSearch] = useState('');
    const searchTerm = onSearchChange ? externalSearch : internalSearch;
    const setSearchTerm = onSearchChange ?? setInternalSearch;

    const filteredConnections = useMemo(() => connections.filter(conn =>
        conn.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        conn.number?.toLowerCase().includes(searchTerm.toLowerCase())
    ), [connections, searchTerm]);

    const getInstanceName = (conn: WhatsAppConnection) =>
        conn.instanceName || conn.config?.instance_name || '';

    const isConnected = (status: string) =>
        status.toLowerCase() === 'conectado' || status.toLowerCase() === 'connected';

    const columns: Column<WhatsAppConnection>[] = useMemo(() => [
        {
            key: 'number',
            header: 'Número',
            render: (conn) => (
                <span className="font-medium text-default">
                    {conn.number || '-'}
                </span>
            )
        },
        {
            key: 'name',
            header: 'Nome',
            render: (conn) => (
                <span className="text-default">{conn.name}</span>
            )
        },
        {
            key: 'status',
            header: 'Status',
            render: (conn) => {
                const connected = isConnected(conn.status);
                return (
                    <div className="flex items-center gap-2">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${connected ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                            {conn.status}
                        </span>
                    </div>
                );
            }
        },
        {
            key: 'photo',
            header: 'Foto de Perfil',
            width: '120px',
            align: 'center',
            render: (conn) => (
                <div className="flex w-full justify-center">
                    <ConnectionPhoto name={conn.name} url={conn.profilePhotoUrl} />
                </div>
            )
        },
        {
            key: 'actions',
            header: 'Ações',
            width: '80px',
            align: 'right',
            render: (conn) => {
                const instanceName = getInstanceName(conn);
                const connected = isConnected(conn.status);

                // Ações diferentes baseado no status
                const actions: ActionMenuItem[] = connected
                    ? [
                        // Quando CONECTADO: Desconectar, Excluir
                        {
                            label: 'Desconectar',
                            icon: <AppIcon name="x-circle" className="w-4 h-4" />,
                            onClick: () => onDisconnect?.(instanceName),
                            show: !!onDisconnect
                        },
                        createDeleteAction(() => onDeleteConnection(conn.id, conn.name, instanceName))
                    ]
                    : [
                        // Quando DESCONECTADO: Conectar, Excluir
                        {
                            label: 'Conectar',
                            icon: <AppIcon name="phone" className="w-4 h-4 text-green-600" />,
                            onClick: () => onReconnect?.(instanceName, conn.id),
                            show: !!onReconnect
                        },
                        createDeleteAction(() => onDeleteConnection(conn.id, conn.name, instanceName))
                    ];

                return <ActionMenu actions={actions} />;
            }
        }
    ], [onDeleteConnection, onDisconnect]);

    const emptyState = (
        <div className="flex flex-col items-center gap-3 py-4">
            <div className="w-16 h-16 bg-surface-subtle rounded-full flex items-center justify-center">
                <WhatsAppIcon className="w-8 h-8" />
            </div>
            <div>
                <p className="text-muted font-medium">Nenhuma conexão encontrada</p>
                <p className="text-muted text-xs mt-1">Clique em "Adicionar conexão" para começar</p>
            </div>
        </div>
    );

    return (
        <div className="h-full flex flex-col">

            <div className="flex-1 overflow-x-auto">
                <DataTable
                    columns={columns}
                    data={filteredConnections}
                    getRowKey={(conn) => conn.id}
                    emptyMessage={emptyState}
                    isLoading={isLoading}
                />
            </div>
        </div>
    );
};

export default ConnectionList;

